/**
 * Admin — Histórico de tempos de ciclo.
 *
 * Alimenta a sugestão de "Tempo de ciclo (s/peça)" no card VA Fundição da
 * cotação. O histórico entra por importação de Excel (colunas: peso peça,
 * peso peça c/ gito, máquina, tempo de ciclo, nº molde) e cada linha pode
 * ser editada ou removida individualmente.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { configAPI } from '@/services/api';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

function errorMessage(err, fallback) {
  return formatApiError(err, fallback);
}

const EMPTY_FORM = {
  part_weight_kg: '',
  part_weight_with_runner_kg: '',
  machine_name: '',
  cycle_time_s: '',
  mold_number: '',
};

function toPayload(form) {
  const num = (v) => parseFloat(String(v ?? '').trim().replace(',', '.'));
  return {
    part_weight_kg: num(form.part_weight_kg),
    part_weight_with_runner_kg: num(form.part_weight_with_runner_kg),
    machine_name: (form.machine_name || '').trim(),
    cycle_time_s: num(form.cycle_time_s),
    mold_number: (form.mold_number || '').trim(),
  };
}

function payloadInvalid(payload) {
  return (
    !Number.isFinite(payload.part_weight_kg) || payload.part_weight_kg <= 0
    || !Number.isFinite(payload.part_weight_with_runner_kg) || payload.part_weight_with_runner_kg <= 0
    || !Number.isFinite(payload.cycle_time_s) || payload.cycle_time_s <= 0
  );
}

export default function CycleTimesTab({ lang }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newForm, setNewForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [replaceOnImport, setReplaceOnImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const reload = () => {
    configAPI
      .getCycleTimes()
      .then((res) => setRecords(res.data || []))
      .catch(() => toast.error(t('load_error', lang)))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await configAPI.importCycleTimes(file, replaceOnImport);
      const { imported, errors } = res.data;
      toast.success(`${t('ct_imported', lang)}: ${imported}`);
      if (errors?.length) {
        toast.warning(`${t('ct_import_skipped', lang)}: ${errors.slice(0, 3).join(' ')}`);
      }
      reload();
    } catch (err) {
      toast.error(errorMessage(err, t('ct_import_error', lang)));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const create = async () => {
    const payload = toPayload(newForm);
    if (payloadInvalid(payload)) {
      toast.error(t('ct_fields_required', lang));
      return;
    }
    try {
      const res = await configAPI.createCycleTime(payload);
      setRecords((prev) => [...prev, res.data]);
      setNewForm(EMPTY_FORM);
      toast.success(t('ct_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      part_weight_kg: String(r.part_weight_kg),
      part_weight_with_runner_kg: String(r.part_weight_with_runner_kg),
      machine_name: r.machine_name || '',
      cycle_time_s: String(r.cycle_time_s),
      mold_number: r.mold_number || '',
    });
  };

  const saveEdit = async (id) => {
    const payload = toPayload(editForm);
    if (payloadInvalid(payload)) {
      toast.error(t('ct_fields_required', lang));
      return;
    }
    try {
      const res = await configAPI.updateCycleTime(id, payload);
      setRecords((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      setEditingId(null);
      toast.success(t('ct_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const remove = async (id) => {
    try {
      await configAPI.deleteCycleTime(id);
      setRecords((prev) => prev.filter((r) => r.id !== id));
      toast.success(t('ct_deleted', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) => setNewForm((p) => ({ ...p, [k]: e.target.value }));
  const setEdit = (k) => (e) => setEditForm((p) => ({ ...p, [k]: e.target.value }));

  if (loading) {
    return (
      <div className="mt-4 space-y-2">
        <Skeleton className="h-32" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('ct_import_title', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('ct_import_hint', lang)}</p>
          <div className="flex flex-wrap items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => importFile(e.target.files?.[0])}
            />
            <Button disabled={importing} onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4" />
              {importing ? t('ct_importing', lang) : t('ct_import_btn', lang)}
            </Button>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={replaceOnImport}
                onCheckedChange={(v) => setReplaceOnImport(Boolean(v))}
              />
              {t('ct_replace_existing', lang)}
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('ct_new_record', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label>{t('ct_part_weight', lang)}</Label>
              <Input inputMode="decimal" value={newForm.part_weight_kg} onChange={setNew('part_weight_kg')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('ct_part_weight_runner', lang)}</Label>
              <Input inputMode="decimal" value={newForm.part_weight_with_runner_kg} onChange={setNew('part_weight_with_runner_kg')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('machine', lang)}</Label>
              <Input value={newForm.machine_name} onChange={setNew('machine_name')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('ct_cycle_time', lang)}</Label>
              <Input inputMode="decimal" value={newForm.cycle_time_s} onChange={setNew('cycle_time_s')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('ct_mold_number', lang)}</Label>
              <Input value={newForm.mold_number} onChange={setNew('mold_number')} />
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ct_part_weight', lang)}</TableHead>
              <TableHead>{t('ct_part_weight_runner', lang)}</TableHead>
              <TableHead>{t('machine', lang)}</TableHead>
              <TableHead>{t('ct_cycle_time', lang)}</TableHead>
              <TableHead>{t('ct_mold_number', lang)}</TableHead>
              <TableHead className="w-[200px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  {t('ct_empty', lang)}
                </TableCell>
              </TableRow>
            )}
            {records.map((r) =>
              editingId === r.id ? (
                <TableRow key={r.id}>
                  <TableCell>
                    <Input inputMode="decimal" value={editForm.part_weight_kg} onChange={setEdit('part_weight_kg')} />
                  </TableCell>
                  <TableCell>
                    <Input inputMode="decimal" value={editForm.part_weight_with_runner_kg} onChange={setEdit('part_weight_with_runner_kg')} />
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.machine_name} onChange={setEdit('machine_name')} />
                  </TableCell>
                  <TableCell>
                    <Input inputMode="decimal" value={editForm.cycle_time_s} onChange={setEdit('cycle_time_s')} />
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.mold_number} onChange={setEdit('mold_number')} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(r.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={r.id}>
                  <TableCell className="tnum">{r.part_weight_kg}</TableCell>
                  <TableCell className="tnum">{r.part_weight_with_runner_kg}</TableCell>
                  <TableCell>{r.machine_name || '—'}</TableCell>
                  <TableCell className="tnum">{r.cycle_time_s}</TableCell>
                  <TableCell>{r.mold_number || '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(r)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                        <Trash2 className="h-3.5 w-3.5" /> {t('delete', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
